import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Input, Select, Tag, Button, Space, Spin, Alert, Drawer, Descriptions, Popconfirm, Switch, Modal, InputNumber, theme } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconMapPin, IconCheck, IconExternalLink, IconMessage } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  getAdminSpaces,
  getAdminSpace,
  approveAdminSpace,
  rejectAdminSpace,
  unpublishAdminSpace,
  reactivateAdminSpace,
  getPlatformSettings,
  IAdminPlatformSettings,
  updatePlatformSettings,
  openAdminConversation,
  IAdminSpaceSummary,
  IAdminSpaceDetail,
  overrideVerifiedBadge,
} from '../../../services/AdminService';
import { maxBookingValueFor } from '../../../interfaces/heavyDutyMachinery/IHostDeposit';
import EmptyState from '../../../components/EmptyState';
import AdminReasonModal from '../../../components/Admin/AdminReasonModal';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { useAppBreakpoint } from '../../../hooks/useAppBreakpoint';
import { SURFACE_1_SOFT } from '../../../theme/creamyTokens';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, numberSorter, dateSorter, enumColumnFilters } from '../../../helpers/adminTableHelpers';

const { Title, Text } = Typography;
const { Search } = Input;

const SPACE_STATUS_COLORS: Record<string, string> = {
  Draft: 'default',
  PendingVerification: 'processing',
  Active: 'green',
  PendingImprovement: 'orange',
  Suspended: 'red',
};

const SPACE_STATUS_LABEL_KEYS: Record<string, string> = {
  Draft: TRANSLATIONS.ADMIN_SPACE_STATUS_DRAFT,
  PendingVerification: TRANSLATIONS.ADMIN_SPACE_STATUS_PENDING_VERIFICATION,
  Active: TRANSLATIONS.ADMIN_SPACE_STATUS_ACTIVE,
  PendingImprovement: TRANSLATIONS.ADMIN_SPACE_STATUS_PENDING_IMPROVEMENT,
  Suspended: TRANSLATIONS.ADMIN_SPACE_STATUS_SUSPENDED,
};

const STATUS_OPTIONS = ['Draft', 'PendingVerification', 'Active', 'PendingImprovement', 'Suspended'];

// Machinery listings filterable by Status, with approve/reject on a listing
// awaiting verification and unpublish/reactivate on a live one.
const SpacesSection: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const [spaces, setSpaces] = useState<IAdminSpaceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const [detail, setDetail] = useState<IAdminSpaceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [unpublishTargetId, setUnpublishTargetId] = useState<string | undefined>(undefined);
  const [rejectTargetId, setRejectTargetId] = useState<string | undefined>(undefined);
  // The badge used to be granted as a by-product of a quality audit. The
  // audits are gone; the badge is not, so Máquinas grants it by hand now --
  // holding the value being moved TO, so the modal knows which way it goes.
  const [badgeTarget, setBadgeTarget] = useState<boolean | undefined>(undefined);

  const [contactOpen, setContactOpen] = useState(false);
  const [contactSubject, setContactSubject] = useState('');
  const [contactText, setContactText] = useState('');
  const [contactSending, setContactSending] = useState(false);

  // The whole settings record, not one flag: the endpoint is a PUT over all
  // of it, so saving needs every field (see updatePlatformSettings).
  const [platformSettings, setPlatformSettings] = useState<IAdminPlatformSettings | null>(null);
  const [savingApprovalMode, setSavingApprovalMode] = useState(false);
  // The fee is typed, not toggled, so it needs somewhere to live while
  // somebody is still typing -- saved on its own button rather than on every
  // keystroke, because a half-typed "1" before the ".5" is a tariff of 1%
  // and would re-price every host on the platform for as long as it took to
  // finish the number.
  const [feePercentDraft, setFeePercentDraft] = useState<number | null>(null);
  const [feeFixedDraft, setFeeFixedDraft] = useState<number | null>(null);
  const [leadDaysDraft, setLeadDaysDraft] = useState<number | null>(null);

  const loadSpaces = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminSpaces(status, search)
      .then(setSpaces)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [status, search]);

  useEffect(() => {
    loadSpaces();
  }, [loadSpaces]);

  useEffect(() => {
    getPlatformSettings()
      .then((loaded) => {
        setPlatformSettings(loaded);
        setFeePercentDraft(loaded.processingFeePercent);
        setFeeFixedDraft(loaded.processingFeeFixed);
        setLeadDaysDraft(loaded.deferredCaptureLeadDays);
      })
      .catch(() => message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One saver for both switches, carrying the settings we already hold and
  // overriding only the field that moved. Sending a single flag was what let
  // a save of one toggle silently reset the other.
  const savePlatformSettings = async (change: Partial<IAdminPlatformSettings>) => {
    if (!platformSettings) return;
    setSavingApprovalMode(true);
    try {
      setPlatformSettings(await updatePlatformSettings({ ...platformSettings, ...change }));
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setSavingApprovalMode(false);
    }
  };

  const openDetail = (id: string) => {
    setDetailLoading(true);
    setDetail({} as IAdminSpaceDetail);
    getAdminSpace(id)
      .then(setDetail)
      .catch(() => {
        message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
        setDetail(null);
      })
      .finally(() => setDetailLoading(false));
  };

  const handleApprove = async (id: string) => {
    try {
      await approveAdminSpace(id);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      if (detail?.id === id) setDetail(null);
      loadSpaces();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleReject = async (reason: string) => {
    if (!rejectTargetId) return;
    try {
      await rejectAdminSpace(rejectTargetId, reason);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      if (detail?.id === rejectTargetId) setDetail(null);
      setRejectTargetId(undefined);
      loadSpaces();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleUnpublish = async (reason: string) => {
    if (!unpublishTargetId) return;
    try {
      await unpublishAdminSpace(unpublishTargetId, reason);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setUnpublishTargetId(undefined);
      loadSpaces();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivateAdminSpace(id);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      loadSpaces();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleOverrideBadge = async (reason: string) => {
    if (!detail || badgeTarget === undefined) return;
    try {
      await overrideVerifiedBadge(detail.id, badgeTarget, reason);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setBadgeTarget(undefined);
      // Both surfaces show the badge, so both have to be told about it.
      setDetail({ ...detail, verifiedBadge: badgeTarget });
      loadSpaces();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleContactHost = async () => {
    if (!detail || !contactText.trim()) return;
    setContactSending(true);
    try {
      await openAdminConversation(detail.hostUserId, contactText.trim(), contactSubject.trim() || undefined, detail.id);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setContactOpen(false);
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_MESSAGES_ACTION_ERROR));
    } finally {
      setContactSending(false);
    }
  };

  // Shared by the desktop "Acciones" column and the mobile card footer.
  const renderActions = (record: IAdminSpaceSummary) => (
    <Space wrap>
      <Button size="small" onClick={() => openDetail(record.id)}>
        {translate(t, TRANSLATIONS.ADMIN_SPACES_VIEW_BUTTON)}
      </Button>
      {record.status === 'PendingVerification' ? (
        <>
          <Popconfirm title={translate(t, TRANSLATIONS.ADMIN_SPACES_APPROVE_CONFIRM)} onConfirm={() => handleApprove(record.id)}>
            <Button size="small" type="primary">
              {translate(t, TRANSLATIONS.ADMIN_SPACES_APPROVE_BUTTON)}
            </Button>
          </Popconfirm>
          <Button size="small" danger onClick={() => setRejectTargetId(record.id)}>
            {translate(t, TRANSLATIONS.ADMIN_SPACES_REJECT_BUTTON)}
          </Button>
        </>
      ) : record.status === 'Suspended' ? (
        <Popconfirm title={translate(t, TRANSLATIONS.ADMIN_SPACES_REACTIVATE_CONFIRM)} onConfirm={() => handleReactivate(record.id)}>
          <Button size="small">{translate(t, TRANSLATIONS.ADMIN_SPACES_REACTIVATE_BUTTON)}</Button>
        </Popconfirm>
      ) : (
        <Button size="small" danger onClick={() => setUnpublishTargetId(record.id)}>
          {translate(t, TRANSLATIONS.ADMIN_SPACES_UNPUBLISH_BUTTON)}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_SPACES)}
      </Title>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 20,
          padding: 16,
          background: token.colorFillQuaternary,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: 8,
        }}
      >
        <Switch
          checked={platformSettings?.requireManualSpaceApproval ?? false}
          loading={platformSettings === null || savingApprovalMode}
          onChange={(checked) => savePlatformSettings({ requireManualSpaceApproval: checked })}
        />
        <div>
          <div style={{ fontWeight: 500 }}>{translate(t, TRANSLATIONS.ADMIN_SPACES_APPROVAL_MODE_LABEL)}</div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {translate(
              t,
              platformSettings?.requireManualSpaceApproval
                ? TRANSLATIONS.ADMIN_SPACES_APPROVAL_MODE_MANUAL_DESCRIPTION
                : TRANSLATIONS.ADMIN_SPACES_APPROVAL_MODE_AUTO_DESCRIPTION
            )}
          </Text>
        </div>
      </div>

      {/* What Stripe charges, which is the number the whole risk model hangs
          off: a host's booking ceiling is derived from it, and so is what a
          cancelling customer is charged. It lived only in the database until
          now, with a guessed default, which meant the one number nobody
          should be guessing was the one nobody could change. */}
      <div
        style={{
          marginBottom: 20,
          padding: 16,
          background: token.colorFillQuaternary,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: 8,
        }}
      >
        <div style={{ fontWeight: 500, marginBottom: 2 }}>{translate(t, TRANSLATIONS.ADMIN_SETTINGS_PROCESSING_FEE_TITLE)}</div>
        <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
          {translate(t, TRANSLATIONS.ADMIN_SETTINGS_PROCESSING_FEE_HELP)}
        </Text>
        <Space wrap align="end">
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {translate(t, TRANSLATIONS.ADMIN_SETTINGS_PROCESSING_FEE_PERCENT)}
            </Text>
            <InputNumber
              value={feePercentDraft}
              onChange={setFeePercentDraft}
              min={0}
              max={10}
              step={0.1}
              precision={2}
              addonAfter="%"
              style={{ width: 150 }}
            />
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {translate(t, TRANSLATIONS.ADMIN_SETTINGS_PROCESSING_FEE_FIXED)}
            </Text>
            <InputNumber
              value={feeFixedDraft}
              onChange={setFeeFixedDraft}
              min={0}
              max={5}
              step={0.05}
              precision={2}
              addonAfter="€"
              style={{ width: 150 }}
            />
          </div>
          <Button
            type="primary"
            loading={savingApprovalMode}
            disabled={feePercentDraft == null || feeFixedDraft == null}
            onClick={() =>
              savePlatformSettings({
                processingFeePercent: feePercentDraft ?? 0,
                processingFeeFixed: feeFixedDraft ?? 0,
              })
            }
          >
            {translate(t, TRANSLATIONS.ADMIN_SETTINGS_SAVE)}
          </Button>
        </Space>
        {/* The abstract tariff made concrete. "1,5% + 0,25" means nothing on
            its own; "100 € of deposit buys 6.650 € of work" is the same fact
            in the units the decision is actually about. */}
        {feePercentDraft != null && feeFixedDraft != null && feePercentDraft > 0 && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 10 }}>
            {t(TRANSLATIONS.ADMIN_SETTINGS_PROCESSING_FEE_EXAMPLE, {
              max: `${maxBookingValueFor(100, feePercentDraft, feeFixedDraft).toFixed(2)} €`,
            })}
          </Text>
        )}
      </div>

      {/* Whether anybody is asked for a deposit at all. Off by default, and
          turning it on locks out every host who has not paid in -- a
          commercial decision with a date, which is why it is audited. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 20,
          padding: 16,
          background: token.colorFillQuaternary,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: 8,
        }}
      >
        <Switch
          checked={platformSettings?.hostDepositRequired ?? false}
          loading={platformSettings === null || savingApprovalMode}
          onChange={(checked) => savePlatformSettings({ hostDepositRequired: checked })}
        />
        <div>
          <div style={{ fontWeight: 500 }}>{translate(t, TRANSLATIONS.ADMIN_SETTINGS_HOST_DEPOSIT_LABEL)}</div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {translate(
              t,
              platformSettings?.hostDepositRequired
                ? TRANSLATIONS.ADMIN_SETTINGS_HOST_DEPOSIT_ON
                : TRANSLATIONS.ADMIN_SETTINGS_HOST_DEPOSIT_OFF
            )}
          </Text>
        </div>
      </div>

      {/* Charging at handover, and how far ahead the funds get frozen. A veto
          like the others: off, and no host can offer it or even see it. */}
      <div
        style={{
          marginBottom: 20,
          padding: 16,
          background: token.colorFillQuaternary,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Switch
            checked={platformSettings?.deferredCaptureEnabled ?? false}
            loading={platformSettings === null || savingApprovalMode}
            onChange={(checked) => savePlatformSettings({ deferredCaptureEnabled: checked })}
          />
          <div>
            <div style={{ fontWeight: 500 }}>{translate(t, TRANSLATIONS.ADMIN_SETTINGS_DEFERRED_CAPTURE_LABEL)}</div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {translate(
                t,
                platformSettings?.deferredCaptureEnabled
                  ? TRANSLATIONS.ADMIN_SETTINGS_DEFERRED_CAPTURE_ON
                  : TRANSLATIONS.ADMIN_SETTINGS_DEFERRED_CAPTURE_OFF
              )}
            </Text>
          </div>
        </div>

        {/* Only worth showing while the thing it configures is switched on. */}
        {platformSettings?.deferredCaptureEnabled && (
          <div style={{ marginTop: 14 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              {translate(t, TRANSLATIONS.ADMIN_SETTINGS_DEFERRED_CAPTURE_LEAD)}
            </Text>
            <Space align="end">
              <InputNumber
                value={leadDaysDraft}
                onChange={setLeadDaysDraft}
                min={1}
                max={6}
                style={{ width: 120 }}
              />
              <Button
                loading={savingApprovalMode}
                disabled={leadDaysDraft == null}
                onClick={() => savePlatformSettings({ deferredCaptureLeadDays: leadDaysDraft ?? 5 })}
              >
                {translate(t, TRANSLATIONS.ADMIN_SETTINGS_SAVE)}
              </Button>
            </Space>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
              {translate(t, TRANSLATIONS.ADMIN_SETTINGS_DEFERRED_CAPTURE_LEAD_HELP)}
            </Text>
          </div>
        )}
      </div>

      {/* The platform-wide kill switch for immediate payment. Turned off,
          every listing behaves as reserva diferida whatever its own switch
          says -- one lever instead of editing every machine on the day
          something is wrong with automatic pricing. It lived only in the
          database and the API until now, which meant the only way to pull it
          was by hand. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 20,
          padding: 16,
          background: token.colorFillQuaternary,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: 8,
        }}
      >
        <Switch
          checked={platformSettings?.immediateBookingEnabled ?? false}
          loading={platformSettings === null || savingApprovalMode}
          onChange={(checked) => savePlatformSettings({ immediateBookingEnabled: checked })}
        />
        <div>
          <div style={{ fontWeight: 500 }}>{translate(t, TRANSLATIONS.ADMIN_SPACES_IMMEDIATE_BOOKING_LABEL)}</div>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {translate(
              t,
              platformSettings?.immediateBookingEnabled
                ? TRANSLATIONS.ADMIN_SPACES_IMMEDIATE_BOOKING_ON_DESCRIPTION
                : TRANSLATIONS.ADMIN_SPACES_IMMEDIATE_BOOKING_OFF_DESCRIPTION
            )}
          </Text>
        </div>
      </div>

      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_SPACES_SEARCH_PLACEHOLDER)}
          style={{ maxWidth: 280, width: '100%' }}
          onSearch={(value) => setSearch(value || undefined)}
        />
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_SPACES_STATUS_FILTER_ALL)}
          style={{ maxWidth: 200, width: '100%' }}
          value={status}
          onChange={setStatus}
          options={STATUS_OPTIONS.map((value) => ({ value, label: translate(t, SPACE_STATUS_LABEL_KEYS[value]) }))}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_SPACES_LOAD_ERROR)} />
      ) : spaces.length === 0 ? (
        <EmptyState
          icon={<IconMapPin size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_SPACES_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_SPACES_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminSpaceSummary>
          rowKey="id"
          dataSource={spaces}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(record) => ({
            key: record.id,
            title: (
              <Space size={6}>
                {record.title}
                {record.verifiedBadge && <IconCheck size={16} color={token.colorSuccess} />}
              </Space>
            ),
            subtitle: record.hostName,
            badge: (
              <Tag color={SPACE_STATUS_COLORS[record.status]} style={{ marginInlineEnd: 0 }}>
                {translate(t, SPACE_STATUS_LABEL_KEYS[record.status])}
              </Tag>
            ),
            fields: [
              { label: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_LOCATION), value: `${record.city}, ${record.country}` },
              { label: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_BOOKINGS), value: record.bookingCount },
              { label: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_CREATED), value: new Date(record.createdAt).toLocaleDateString() },
            ],
            actions: renderActions(record),
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_TITLE),
              dataIndex: 'title',
              key: 'title',
              sorter: textSorter<IAdminSpaceSummary>('title'),
              render: (value: string, record: IAdminSpaceSummary) => (
                <Space>
                  <Text>{value}</Text>
                  {record.verifiedBadge && <IconCheck size={16} color={token.colorSuccess} />}
                </Space>
              ),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_HOST),
              dataIndex: 'hostName',
              key: 'hostName',
              sorter: textSorter<IAdminSpaceSummary>('hostName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_LOCATION),
              key: 'location',
              sorter: (a: IAdminSpaceSummary, b: IAdminSpaceSummary) => `${a.country} ${a.city}`.localeCompare(`${b.country} ${b.city}`),
              render: (_: unknown, record: IAdminSpaceSummary) => `${record.city}, ${record.country}`,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_STATUS),
              dataIndex: 'status',
              key: 'status',
              filters: enumColumnFilters(STATUS_OPTIONS, (value) => translate(t, SPACE_STATUS_LABEL_KEYS[value])),
              onFilter: (value, record: IAdminSpaceSummary) => record.status === value,
              render: (value: string) => <Tag color={SPACE_STATUS_COLORS[value]}>{translate(t, SPACE_STATUS_LABEL_KEYS[value])}</Tag>,
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_BOOKINGS),
              dataIndex: 'bookingCount',
              key: 'bookingCount',
              sorter: numberSorter<IAdminSpaceSummary>('bookingCount'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_CREATED),
              dataIndex: 'createdAt',
              key: 'createdAt',
              sorter: dateSorter<IAdminSpaceSummary>('createdAt'),
              render: (value: string) => new Date(value).toLocaleDateString(),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_ACTIONS),
              key: 'actions',
              render: (_: unknown, record: IAdminSpaceSummary) => renderActions(record),
            },
          ]}
        />
      )}

      <Drawer
        title={translate(t, TRANSLATIONS.ADMIN_SPACES_DETAIL_TITLE)}
        open={detail !== null}
        onClose={() => setDetail(null)}
        width={isMobile ? '100%' : 520}
      >
        {detailLoading || !detail?.id ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : (
          <>
            <Space style={{ marginBottom: 16 }} wrap>
              <Button
                icon={<IconExternalLink size={16} />}
                onClick={() => window.open(`/heavy-duty-machinery/${detail.id}`, '_blank', 'noopener')}
              >
                {translate(t, TRANSLATIONS.ADMIN_SPACES_PREVIEW_BUTTON)}
              </Button>
              <Button
                icon={<IconMessage size={16} />}
                onClick={() => {
                  setContactSubject(detail.title);
                  setContactText('');
                  setContactOpen(true);
                }}
              >
                {translate(t, TRANSLATIONS.ADMIN_MESSAGES_CONTACT_BUTTON)}
              </Button>
            </Space>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: 16,
                padding: '10px 12px',
                borderRadius: 12,
                background: SURFACE_1_SOFT,
              }}
            >
              <div>
                <Text strong style={{ display: 'block' }}>
                  {translate(t, TRANSLATIONS.ADMIN_SPACES_BADGE_TITLE)}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {translate(t, TRANSLATIONS.ADMIN_SPACES_BADGE_HINT)}
                </Text>
              </div>
              <Switch checked={detail.verifiedBadge} onChange={(checked) => setBadgeTarget(checked)} />
            </div>

            {detail.status === 'PendingVerification' && (
              <Space style={{ marginBottom: 16 }}>
                <Popconfirm
                  title={translate(t, TRANSLATIONS.ADMIN_SPACES_APPROVE_CONFIRM)}
                  onConfirm={() => handleApprove(detail.id)}
                >
                  <Button type="primary">{translate(t, TRANSLATIONS.ADMIN_SPACES_APPROVE_BUTTON)}</Button>
                </Popconfirm>
                <Button danger onClick={() => setRejectTargetId(detail.id)}>
                  {translate(t, TRANSLATIONS.ADMIN_SPACES_REJECT_BUTTON)}
                </Button>
              </Space>
            )}

            {detail.photoThumbnails.length > 0 && (
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 24 }}>
                {detail.photoThumbnails.map((url, index) => (
                  <img
                    key={url + index}
                    src={url}
                    alt=""
                    style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
                  />
                ))}
              </div>
            )}

            <Descriptions column={1} bordered size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_SPACES_DETAIL_HOST_LABEL)}>
                {detail.hostName}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_SPACES_DETAIL_HOST_EMAIL_LABEL)}>
                {detail.hostEmail ? <a href={`mailto:${detail.hostEmail}`}>{detail.hostEmail}</a> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_SPACES_DETAIL_HOST_PHONE_LABEL)}>
                {detail.hostPhone ? <a href={`tel:${detail.hostPhone}`}>{detail.hostPhone}</a> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_SPACES_DETAIL_ADDRESS_LABEL)}>
                {detail.privateAddress || detail.formattedAddress}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_SPACES_DETAIL_RENTAL_MODE_LABEL)}>
                {detail.rentalMode}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_SPACES_DETAIL_PRICE_LABEL)}>
                {[
                  detail.pricePerHour != null ? `${detail.pricePerHour} €/h` : null,
                  // "€/noche" was the jacuzzi app's unit. The column is
                  // still called PricePerNight in the database (see
                  // rentalPeriodHelper for why that name stays), but a
                  // machine is priced by the day.
                  detail.pricePerNight != null ? `${detail.pricePerNight} €/día` : null,
                  detail.pricePerDayPass != null ? `${detail.pricePerDayPass} €/pase` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_SPACES_COL_STATUS)}>
                <Tag color={SPACE_STATUS_COLORS[detail.status]}>{translate(t, SPACE_STATUS_LABEL_KEYS[detail.status])}</Tag>
              </Descriptions.Item>
              {detail.suspensionReason && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_SPACES_DETAIL_SUSPENSION_REASON_LABEL)}>
                  {detail.suspensionReason}
                </Descriptions.Item>
              )}
            </Descriptions>

          </>
        )}
      </Drawer>

      <AdminReasonModal
        open={unpublishTargetId !== undefined}
        title={translate(t, TRANSLATIONS.ADMIN_SPACES_UNPUBLISH_MODAL_TITLE)}
        confirmLabel={translate(t, TRANSLATIONS.ADMIN_SPACES_UNPUBLISH_BUTTON)}
        onCancel={() => setUnpublishTargetId(undefined)}
        onConfirm={handleUnpublish}
      />

      <AdminReasonModal
        open={badgeTarget !== undefined}
        title={translate(
          t,
          badgeTarget ? TRANSLATIONS.ADMIN_SPACES_BADGE_GRANT_MODAL_TITLE : TRANSLATIONS.ADMIN_SPACES_BADGE_REVOKE_MODAL_TITLE,
        )}
        confirmLabel={translate(
          t,
          badgeTarget ? TRANSLATIONS.ADMIN_SPACES_BADGE_GRANT_BUTTON : TRANSLATIONS.ADMIN_SPACES_BADGE_REVOKE_BUTTON,
        )}
        onCancel={() => setBadgeTarget(undefined)}
        onConfirm={handleOverrideBadge}
      />

      <AdminReasonModal
        open={rejectTargetId !== undefined}
        title={translate(t, TRANSLATIONS.ADMIN_SPACES_REJECT_MODAL_TITLE)}
        confirmLabel={translate(t, TRANSLATIONS.ADMIN_SPACES_REJECT_BUTTON)}
        onCancel={() => setRejectTargetId(undefined)}
        onConfirm={handleReject}
      />

      <Modal
        title={translate(t, TRANSLATIONS.ADMIN_MESSAGES_CONTACT_TITLE)}
        open={contactOpen}
        onCancel={() => setContactOpen(false)}
        onOk={handleContactHost}
        okButtonProps={{ loading: contactSending, disabled: !contactText.trim() }}
        okText={translate(t, TRANSLATIONS.ADMIN_MESSAGES_CONTACT_SEND_BUTTON)}
      >
        <div style={{ marginBottom: 12 }}>
          <Text>{translate(t, TRANSLATIONS.ADMIN_MESSAGES_CONTACT_SUBJECT_LABEL)}</Text>
          <Input value={contactSubject} onChange={(e) => setContactSubject(e.target.value)} />
        </div>
        <div>
          <Text>{translate(t, TRANSLATIONS.ADMIN_MESSAGES_CONTACT_MESSAGE_LABEL)}</Text>
          <Input.TextArea rows={4} value={contactText} onChange={(e) => setContactText(e.target.value)} />
        </div>
      </Modal>
    </div>
  );
};

export default SpacesSection;
